import * as React from "react";
import {
    Link,
    useLoaderData,
    useNavigate,
} from "react-router-dom";
import { Button, Icon, Input, Table } from "semantic-ui-react";
import "./projects.css";
import { RootStore } from "../../../stores/root-store";
import {
    CreationProject,
    ICmiDocumentationRawi,
    ICmiDocumentationRawiUwe,
    Project
} from "../../../stores/project/project";
import { Constants } from "../../../globals/constants";
import { useItemFilter } from "../../../hooks/use-item-filter";
import SVG from "react-inlinesvg";
import actionStatusIcon from "../../../assets/images/icons/plain/clipboard_check_edit.svg";
import updatedIcon from "../../../assets/images/icons/selected/arrow_circle2.svg";
import ActionStatusIcon from "../../../components/action-status-icon/action-status-icon";
import FilterConfiguration from "../../../components/filter-configuration/filter-configuration";
import { AppState } from "../../../stores/app-mobx";
import { getLabel } from "../../../reflection/reflection";
import { observer } from "mobx-react";
import { useEffect } from "react";

const filtersKey = "projects";
const searchTextKey = "projects";
const sortAttributeKey = "projects";

type LoadResult = {
    projects: Project[];
    municipalityNames: string[];
    projectTypes: string[];
    status: string[];
    actionStatus: string[];
    appStore: AppState;
};

function projectSearchFilter(project: Project, searchText: string) {
    const searchTextLowerCase = searchText.toLowerCase();

    return (
        project.id.toString().includes(searchTextLowerCase) ||
        project.name.toLowerCase().includes(searchTextLowerCase) ||
        project.municipalityName.toLowerCase().includes(searchTextLowerCase) ||
        project.ebageNr?.toLowerCase().includes(searchTextLowerCase) ||
        ("cmiNumberRawi" in project && (project as ICmiDocumentationRawi).cmiNumberRawi?.toLowerCase().includes(searchTextLowerCase)) ||
        ("cmiDocumentsRawiPre" in project && (
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsRawiPre.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsRawiApproved.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsUwePre.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsUweApproved.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase))
        ))
    );
}

export async function loader(request: Request, rootStore: RootStore, appStore: AppState) {
    const rootStoreSession = rootStore.newSession();
    const municipalityNames = (await rootStoreSession.municipalityStore.loadAll()).map(m => m.name);
    const projects = (await rootStoreSession.projectStore.loadAll()).filter(p => p.typeKey !== Constants.projectTypeRecultivation);
    await rootStoreSession.areaStore.loadAll();
    await rootStoreSession.compensationStore.loadAll();
    await rootStoreSession.contactStore.loadAll();
    await rootStoreSession.userStore.loadAll();
    await rootStoreSession.activityLogEntryStore.loadAll();
    const projectTypes = rootStoreSession.keyValueStore.projectTypes.map(i => i.value);
    const status = rootStoreSession.keyValueStore.projectStatuses.map(i => i.value);
    const actionStatus = rootStoreSession.keyValueStore.actionStatus.map(i => i.value);
    appStore.setShowMap(appStore.userShowMap);
    return { projects, municipalityNames, projectTypes, status, actionStatus, appStore };
}

const Projects = observer(() => {
    const {
        projects,
        municipalityNames,
        projectTypes,
        status,
        actionStatus,
        appStore
    } = useLoaderData() as LoadResult;
    const filters = appStore.filters.get(filtersKey) ?? [];
    const searchText = appStore.searchTexts.get(searchTextKey) ?? "";
    const sortConfiguration = appStore.sortAttributes.get(sortAttributeKey) ?? {
        attribute: "name",
        direction: "ascending"
    };
    const [state, dispatch] = useItemFilter<Project>(sortConfiguration.attribute, sortConfiguration.direction, projects, filters);
    const { sortAttribute, sortDirection, items } = state;
    const navigate = useNavigate();
    const filterOptions = [
        {
            attribute: "fullType",
            attributeDisplayText: getLabel(CreationProject.prototype, "fullType"),
            values: projectTypes
        },
        {
            attribute: "municipalityName",
            attributeDisplayText: getLabel(CreationProject.prototype, "municipalityName"),
            values: municipalityNames
        },
        {
            attribute: "actionStatusDisplayText",
            attributeDisplayText: getLabel(CreationProject.prototype, "actionStatusDisplayText"),
            values: actionStatus
        },
        {
            attribute: "status",
            attributeDisplayText: getLabel(CreationProject.prototype, "status"),
            values: status
        }
    ];

    useEffect(() => {
        appStore.setSortConfiguration(sortAttributeKey, { attribute: sortAttribute, direction: sortDirection });
    }, [sortAttribute, sortDirection]);


    return (<div className="projectsMain">
            <h1>Projekte</h1>
            <Link to="neu">
                <Button type="submit" size="large">
                    Neues Projekt
                </Button>
            </Link>
            <FilterConfiguration
                filterOptions={filterOptions}
                initialFilters={filters}
                onFilterAdded={(f) => {
                    dispatch({
                        type: "addFilter",
                        filter: f
                    });
                    appStore.addFilter(filtersKey, f);
                }}
                onFilterRemoved={(f) => {
                    dispatch({
                        type: "removeFilter",
                        id: f.id,
                    });
                    appStore.removeFilter(filtersKey, f);
                }}/>
            <div>
                <Input
                    id="search-field"
                    placeholder="Suche nach ID, Name, Gemeinde, Baugesuchsnummer, CMI-Nummer"
                    defaultValue={searchText}
                    type="search"
                    icon={
                        <Icon
                            name="delete"
                            link
                            onClick={() => {
                                const input = document.getElementById(
                                    "search-field"
                                ) as HTMLInputElement;
                                input.value = "";
                                appStore.setSearchText(searchTextKey, "");
                            }}
                        />
                    }
                    onChange={(event) => {
                        appStore.setSearchText(searchTextKey, event.target.value);
                    }}
                    style={{ width: "800px" }}
                />
                <Table celled={true} collapsing={true} compact="very" selectable={true} singleLine={true}
                       size="small"
                       sortable={true}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell
                                sorted={sortAttribute === "id" ? sortDirection : null}
                                onClick={() => {
                                    dispatch({ type: "changeSort", sortAttribute: "id" });
                                }}
                            >
                                {getLabel(CreationProject.prototype, "id")}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortAttribute === "name" ? sortDirection : null}
                                onClick={() => {
                                    dispatch({ type: "changeSort", sortAttribute: "name" });
                                }}
                            >
                                {getLabel(CreationProject.prototype, "name")}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortAttribute === "type" ? sortDirection : null}
                                onClick={() => {
                                    dispatch({ type: "changeSort", sortAttribute: "type" });
                                }}
                            >
                                {getLabel(CreationProject.prototype, "fullType")}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortAttribute === "municipalityName" ? sortDirection : null}
                                onClick={() => {
                                    dispatch({ type: "changeSort", sortAttribute: "municipalityName" });
                                }}
                            >
                                {getLabel(CreationProject.prototype, "municipalityName")}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortAttribute === "status" ? sortDirection : null}
                                onClick={() => {
                                    dispatch({ type: "changeSort", sortAttribute: "status" });
                                }}
                            >
                                {getLabel(CreationProject.prototype, "status")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "ebageNr" ? sortDirection : null}
                                              onClick={() => {
                                                  dispatch({ type: "changeSort", sortAttribute: "ebageNr" });
                                              }}
                            >
                                {getLabel(CreationProject.prototype, "ebageNr")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "actionStatus" ? sortDirection : null}
                                              onClick={() => {
                                                  dispatch({ type: "changeSort", sortAttribute: "actionStatus" });
                                              }}
                                              textAlign="center">
                                <SVG src={actionStatusIcon} height="auto" width={20}
                                     title={getLabel(CreationProject.prototype, "actionStatusDisplayText")}/>
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortAttribute === "updatedAtTimestamp" ? sortDirection : null}
                                onClick={() => {
                                    dispatch({ type: "changeSort", sortAttribute: "updatedAtTimestamp" });
                                }}
                                textAlign="center">
                                <SVG src={updatedIcon} height="auto" width={20}
                                     title={getLabel(CreationProject.prototype, "updatedAt")}/>
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {items.filter(p => projectSearchFilter(p, searchText)).map((p) => (
                            <Table.Row
                                key={`${p.categoryKey}-${p.id}`}
                                onClick={() => navigate(`${p.categoryKey}/${p.id}`)}>
                                <Table.Cell>{p.id}</Table.Cell>
                                <Table.Cell>{p.name}</Table.Cell>
                                <Table.Cell>{p.type}</Table.Cell>
                                <Table.Cell>{p.municipalityName}</Table.Cell>
                                <Table.Cell>{p.status}</Table.Cell>
                                <Table.Cell>{p.ebageNr}</Table.Cell>
                                <Table.Cell textAlign="center">
                                    <ActionStatusIcon actionStatus={p.actionStatus} issues={p.issues}/>
                                </Table.Cell>
                                <Table.Cell textAlign="center">{p.updatedAt}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        </div>
    );
});

export default Projects;
